import { Cloud, CloudDrizzle, CloudFog, CloudRain, CloudSnow, CloudLightning, Sun } from "lucide-react";
import { motion } from "framer-motion";
import type { DailyWeather, WeatherKind } from "@/lib/weather";
import { cn } from "@/lib/utils";

const iconFor: Record<WeatherKind, typeof Sun> = {
  clear: Sun,
  cloudy: Cloud,
  fog: CloudFog,
  drizzle: CloudDrizzle,
  rain: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
};

/**
 * Inline weather pill for Timeline day headers.
 *   [☀️  22° / 14°]
 * Silently renders nothing if no data is passed.
 *
 * Entrance: fade + slight scale so pills arrive gently as the timeline
 * loads, without a hard pop-in when the weather query resolves.
 */
export function WeatherPill({
  weather,
  className,
}: {
  weather?: DailyWeather | undefined;
  className?: string;
}) {
  if (!weather) return null;
  const Icon = iconFor[weather.kind];
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-bg-sunken px-2.5 py-0.5",
        "text-[11px] font-semibold text-ink-muted tabular",
        className,
      )}
      title={`${weather.kind} · high ${weather.tempMax}° / low ${weather.tempMin}°`}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {weather.tempMax}° / {weather.tempMin}°
    </motion.span>
  );
}
